import {
  doc,
  setDoc,
  Timestamp,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { COLLECTIONS } from '../collections';
import { handleDatabaseError } from '@/lib/utils/errors/handlers';
import { StripeSubscription } from './stripeSubscription';

export type SubscriptionTier = 'FREE' | 'BASIC' | 'PRO';

export interface UserSubscription {
  tier: SubscriptionTier;
  startDate: Timestamp;
  endDate?: Timestamp;
  autoRenew: boolean;
  analysisCount: number;
  lastResetDate: Timestamp;
  features: string[];
}

export const SUBSCRIPTION_PLANS = {
  FREE: {
    tier: 'FREE' as const,
    features: [
      'Up to 5 website analyses per month',
      'Basic copy recommendations',
      'Email support',
    ],
    analysisLimit: 5,
    price: 0,
  },
  BASIC: {
    tier: 'BASIC' as const,
    features: [
      'Unlimited website analyses',
      'Advanced copy recommendations',
      'Priority support',
      'A/B testing suggestions',
      'Team collaboration',
    ],
    analysisLimit: Infinity,
    price: 29,
  },
  PRO: {
    tier: 'PRO' as const,
    features: [
      'Custom solutions',
      'Dedicated account manager',
      'API access',
      'Custom integrations',
      'SLA guarantees',
    ],
    analysisLimit: Infinity,
    price: 99,
  },
} as const;

export async function initializeUserSubscription(
  userId: string
): Promise<UserSubscription> {
  try {
    const subscription: UserSubscription = {
      tier: 'FREE',
      startDate: Timestamp.now(),
      autoRenew: false,
      analysisCount: 0,
      lastResetDate: Timestamp.now(),
      features: SUBSCRIPTION_PLANS.FREE.features,
    };

    const userRef = doc(db, COLLECTIONS.USERS, userId);
    await setDoc(userRef, { subscription }, { merge: true });

    return subscription;
  } catch (error) {
    throw handleDatabaseError(error);
  }
}

const regexPro = /\bpro\b/;
const regexBasic = /\bbasic\b/;

function nameToTier(n: string): SubscriptionTier {
  if (regexPro.test(n.toLowerCase())) return 'PRO';
  if (regexBasic.test(n.toLocaleLowerCase())) return 'BASIC';
  return 'FREE';
}

export function toUserSubscription(d: StripeSubscription): UserSubscription {
  const product = d.items[0].price.product;
  return {
    tier: nameToTier(product.name),
    startDate: d.current_period_start,
    endDate: d.current_period_end,
    autoRenew: !d.cancel_at_period_end && d.status === 'active',
    analysisCount: parseInt(product.metadata.analysisCount, 10),
    lastResetDate: d.current_period_start,
    features: product.features.map((x) => x.name),
  };
}

export async function getUserSubscription(
  userId: string
): Promise<UserSubscription | null> {
  try {
    const subscriptionsRef = collection(
      db,
      COLLECTIONS.USERS,
      userId,
      'subscriptions'
    );
    const q = query(
      subscriptionsRef,
      orderBy('created', 'desc'), // Order by created timestamp in descending order
      limit(1) // Get only the most recent one
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null; // No subscriptions found
    }

    const latestDoc = querySnapshot.docs[0];
    const data = latestDoc.data() as StripeSubscription;
    return toUserSubscription(data);
  } catch (error) {
    throw handleDatabaseError(error);
  }
}

export async function updateUserSubscription(
  userId: string,
  subscription: Partial<UserSubscription>
): Promise<void> {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    await setDoc(
      userRef,
      {
        subscription: {
          ...subscription,
          features:
            SUBSCRIPTION_PLANS[subscription.tier as SubscriptionTier].features,
        },
      },
      { merge: true }
    );
  } catch (error) {
    throw handleDatabaseError(error);
  }
}
